// v2-osm-fix 2026-04-24
import React, { useCallback, useEffect, useRef, useState } from "react";
import { noterActivite, EXPORTS } from "../utilities/journalActivite";
import { Box, Typography } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { getAllLocations, getCarteLieux } from "../utilities/locationService";
import { modeleTuilesLeaflet, sousDomainesLeaflet, attributionFond } from "../utilities/basemap";
import { messageServeur } from "../utilities/userService";
import { useDispatch } from "react-redux";
import { loaderOff, loaderOn, openSnackbar } from "../redux/actions";
import { Data, enMillis, statusColor, toDataRow, useFicheLieu } from "./lieux/logiqueLieux";
import { FicheLieuModal } from "./lieux/composantsLieux";
import EnTetePage from "../components/charte/EnTetePage";
import Bloc from "../components/charte/Bloc";
import TitreSection from "../components/charte/TitreSection";
import { BoutonSecondaire } from "../components/charte/Boutons";
import { AIDE_ANALYSES } from "../aide/textesAide";

/* Analyses › Carte des lieux. La file de validation, qui partageait cette
   page (onglet « Validation »), est désormais À traiter › Lieux à valider
   (pages/lieux/LieuxAValider.tsx) ; la fiche d'un lieu ouverte depuis la
   carte garde les mêmes actions (logiqueLieux.ts). */

// ── Constants ──────────────────────────────────────────────────────────────

type Category = "pharmacie" | "restaurant" | "bureau de poste" | "marché" | "autre";

const CATEGORY_COLOR: Record<Category, string> = {
  pharmacie: "#8B5CF6",
  restaurant: "#F59E0B",
  "bureau de poste": "#14B8A6",
  marché: "#3B82F6",
  autre: "#6B7280",
};

function categoryColor(cat: string): string {
  const key = (cat ?? "").toLowerCase() as Category;
  return CATEGORY_COLOR[key] ?? CATEGORY_COLOR["autre"];
}

// Signale la zone visible au chargement puis à chaque déplacement ou zoom.
type Zone = { minLat: number; maxLat: number; minLng: number; maxLng: number; zoom: number };

function SuiviZone({ onZone }: { onZone: (z: Zone) => void }) {
  const map = useMap();
  const signaler = useCallback(() => {
    const b = map.getBounds();
    // Leaflet dépasse ±180 en zoom arrière : le serveur refuse une zone hors bornes.
    onZone({
      minLat: Math.max(-90, b.getSouth()), maxLat: Math.min(90, b.getNorth()),
      minLng: Math.max(-180, b.getWest()), maxLng: Math.min(180, b.getEast()),
      zoom: map.getZoom(),
    });
  }, [map, onZone]);
  useMapEvents({ moveend: signaler });
  useEffect(() => { signaler(); }, [signaler]);
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════

export default function Location() {
  const dispatch = useDispatch();

  const [mapRows, setMapRows] = useState<Data[]>([]);
  const [osmRows, setOsmRows] = useState<any[]>([]);
  const [carteTronquee, setCarteTronquee] = useState(false);
  const [carteTotal, setCarteTotal] = useState(0);
  const [carteChargement, setCarteChargement] = useState(false);

  /* Carte : seule la zone visible est demandée ; au-delà du plafond serveur,
     un échantillon réparti sur la zone et l'invitation à zoomer. */
  const derniereZone = useRef<Zone | null>(null);
  const minuterieZone = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requeteCarte = useRef(0);
  const fetchCarte = async (zone: Zone) => {
    const n = ++requeteCarte.current;
    setCarteChargement(true);
    try {
      const res = await getCarteLieux(zone);
      if (n !== requeteCarte.current) return;
      const pts: any[] = Array.isArray(res.points) ? res.points : [];
      // Points légers : le montant à créditer est lu avec la fiche complète à l'ouverture.
      setMapRows(pts.filter(p => p.createdBy !== "osm_import").map((p, i) => ({ ...toDataRow(p, i), aCrediter: undefined })));
      setOsmRows(pts.filter(p => p.createdBy === "osm_import"));
      setCarteTronquee(!!res.tronque);
      setCarteTotal(res.total ?? pts.length);
    } catch (error) {
      if (n === requeteCarte.current) dispatch(openSnackbar(messageServeur(error, "Impossible de charger la carte."), "red"));
    } finally {
      if (n === requeteCarte.current) setCarteChargement(false);
    }
  };
  const surZone = useCallback((zone: Zone) => {
    derniereZone.current = zone;
    if (minuterieZone.current) clearTimeout(minuterieZone.current);
    minuterieZone.current = setTimeout(() => fetchCarte(zone), 400);
  }, []);
  useEffect(() => () => { if (minuterieZone.current) clearTimeout(minuterieZone.current); }, []);

  // Après une décision prise depuis la fiche : la zone de carte affichée.
  const fiche = useFicheLieu(() => { if (derniereZone.current) fetchCarte(derniereZone.current); });

  // ── Map / Carte state ───────────────────────────────────────────────────

  const [layerMode, setLayerMode] = useState<'tribe' | 'osm' | 'both'>('both');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const mapWithCoords = mapRows.filter(r => r.latitude != null && r.longitude != null);

  const categoryCounts: Record<string, number> = {};
  mapWithCoords.forEach(r => {
    const k = (r.Category ?? "autre").toLowerCase();
    categoryCounts[k] = (categoryCounts[k] ?? 0) + 1;
  });

  const filteredMapRows = selectedCategory
    ? mapWithCoords.filter(r => (r.Category ?? "autre").toLowerCase() === selectedCategory)
    : mapWithCoords;

  const kpiRows = selectedCategory
    ? mapRows.filter(r => (r.Category ?? "autre").toLowerCase() === selectedCategory)
    : mapRows;

  const avgFreshness = (() => {
    const withDate = kpiRows.filter(r => r.createdOn != null && !isNaN(enMillis(r.createdOn)));
    if (!withDate.length) return null;
    const avg = withDate.reduce((sum, r) => {
      const days = (Date.now() - enMillis(r.createdOn!)) / 86400000;
      return sum + days;
    }, 0) / withDate.length;
    return Math.round(avg);
  })();

  // L'export reste complet (tous les lieux Tribe), lu à la demande : la carte n'a que la zone visible.
  const exportCSV = async () => {
    let tous: Data[];
    dispatch(loaderOn());
    try {
      const raw = await getAllLocations();
      tous = (Array.isArray(raw) ? raw : []).filter((p: any) => p.createdBy !== "osm_import").map(toDataRow);
    } catch (error) {
      dispatch(openSnackbar(messageServeur(error, "Export impossible : lieux illisibles."), "red"));
      return;
    } finally {
      dispatch(loaderOff());
    }
    const src = selectedCategory ? tous.filter(r => (r.Category ?? "autre").toLowerCase() === selectedCategory) : tous;
    const header = ["Lieu", "Catégorie", "Adresse", "Tips", "Statut", "Spotter"];
    const lines = src.map(r => [r.Location_Name, r.Category, r.Location_Address, r.Token, r.Status, r.createdBy ?? ""].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "locations.csv"; a.click();
    URL.revokeObjectURL(url);
    noterActivite("export", EXPORTS.lieuxCsv);
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 } }}>
      <EnTetePage
        domaine="analyses"
        titre="Carte des lieux"
        phrase="Les lieux de la zone visible ; un clic sur un point ouvre sa fiche."
        aide={AIDE_ANALYSES.carte}
      />

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
        {/* Carte */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Choix des couches */}
          <Box sx={{ display: "flex", gap: 1 }} role="group" aria-label="Couches affichées">
            {([['tribe', 'Tribe'], ['osm', 'Référentiel'], ['both', 'Les deux']] as const).map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setLayerMode(mode)}
                aria-pressed={layerMode === mode}
                style={{
                  padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  background: layerMode === mode ? "#1F1E1B" : "transparent",
                  color: layerMode === mode ? "#fff" : "#1F1E1B",
                  border: "1.5px solid #1F1E1B",
                }}
              >
                {label}
              </button>
            ))}
          </Box>

          <Box sx={{ borderRadius: "8px", overflow: "hidden", border: "1.5px solid var(--c-text-primary)" }}>
            <MapContainer
              bounds={[[4, -18], [18, 5]]}
              style={{ height: "520px", width: "100%" }}
              scrollWheelZoom={true}
            >
              <SuiviZone onZone={surZone} />
              <TileLayer url={modeleTuilesLeaflet()} attribution={attributionFond}
                {...({ subdomains: sousDomainesLeaflet() } as any)} />
              {(layerMode === 'osm' || layerMode === 'both') && osmRows
                .filter(r => (r.latitude ?? r.lat) != null && (r.longitude ?? r.lng) != null)
                .map((r, i) => (
                  <CircleMarker
                    key={`osm-${i}`}
                    center={[r.latitude ?? r.lat, r.longitude ?? r.lng]}
                    radius={4}
                    pathOptions={{ fillColor: "#1D5FA4", color: "#1D5FA4", fillOpacity: 0.5, weight: 1 }}
                  >
                    <Tooltip direction="top" offset={[0, -4]}>
                      <span style={{ fontWeight: 600 }}>{r.locationName ?? r.name}</span>
                      <br />{r.locationType ?? r.type} · référentiel
                    </Tooltip>
                  </CircleMarker>
                ))}
              {(layerMode === 'tribe' || layerMode === 'both') && filteredMapRows.map((row) => (
                <CircleMarker
                  key={row.referenceId}
                  center={[row.latitude!, row.longitude!]}
                  radius={8}
                  pathOptions={{
                    fillColor: statusColor(row.Status),
                    color: "#1F1E1B",
                    fillOpacity: 0.85,
                    weight: 1,
                  }}
                  eventHandlers={{ click: () => fiche.ouvrirFiche(row) }}
                >
                  <Tooltip direction="top" offset={[0, -6]}>
                    <span style={{ fontWeight: 600 }}>{row.Location_Name}</span>
                    <br />{row.Category} · {row.Location_Address}
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </Box>

          {carteTronquee && (
            <Typography sx={{ color: "var(--c-warning)", fontSize: "12px", fontWeight: 600, px: 0.5 }}>
              {`${(mapRows.length + osmRows.length).toLocaleString("fr-FR")} lieux affichés sur ${carteTotal.toLocaleString("fr-FR")} dans cette zone : zoomez pour voir plus.`}
            </Typography>
          )}
          {carteChargement && (
            <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "12px", px: 0.5 }}>Chargement de la zone…</Typography>
          )}

          {/* Légende */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", px: 0.5 }}>
            {[
              { color: "#356A0F", label: "Approuvé" },
              { color: "#7C5600", label: "En attente" },
              { color: "#A23A0D", label: "Rejeté" },
              { color: "#1D5FA4", label: "OpenStreetMap" },
            ].map(({ color, label }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: color, border: "1px solid #1F1E1B", flexShrink: 0 }} />
                <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "12px" }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Panneau latéral */}
        <Box sx={{ width: { xs: "100%", lg: 260 }, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <Bloc sx={{ p: 2 }}>
            <TitreSection titre="Catégories" />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {[null, ...Object.keys(categoryCounts)].map(cat => {
                const isAll = cat === null;
                const active = isAll ? selectedCategory === null : selectedCategory === cat;
                const count = isAll ? mapWithCoords.length : categoryCounts[cat!];
                const color = isAll ? "#1F1E1B" : categoryColor(cat!);
                return (
                  <Box
                    key={cat ?? "all"}
                    component="button"
                    type="button"
                    onClick={() => setSelectedCategory(isAll ? null : cat!)}
                    aria-pressed={active}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 1.25, py: 0.6, borderRadius: "6px", cursor: "pointer", font: "inherit",
                      background: active ? "var(--c-surface-3)" : "transparent",
                      border: active ? "1.5px solid #1F1E1B" : "1px solid var(--c-border)",
                      color: "var(--c-text-primary)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {!isAll && <Box sx={{ width: 8, height: 8, background: color }} />}
                      <Typography sx={{ fontSize: "12.5px", fontWeight: active ? 700 : 400 }}>
                        {isAll ? "Toutes" : cat}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "12px", fontWeight: 600 }}>{count}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Bloc>

          <Bloc sx={{ p: 2 }}>
            <TitreSection titre={`Zone visible${carteTronquee ? " (échantillon)" : ""}`} />
            {[
              { label: "Lieux", value: kpiRows.length },
              { label: "Approuvés", value: kpiRows.filter(r => r.Status === "approved").length },
              { label: "Âge moyen", value: avgFreshness != null ? `${avgFreshness} j` : "—" },
            ].map(kpi => (
              <Box key={kpi.label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "13px" }}>{kpi.label}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>{kpi.value}</Typography>
              </Box>
            ))}
          </Bloc>

          <BoutonSecondaire onClick={exportCSV} fullWidth>
            Exporter tous les lieux (CSV)
          </BoutonSecondaire>
        </Box>
      </Box>

      <FicheLieuModal fiche={fiche} onRelance={() => { if (derniereZone.current) fetchCarte(derniereZone.current); }} />
    </Box>
  );
}
